// The product's changes waiting for review (API catalog lib/product-changes), opened from the chip under the
// dialog's title: what they change, whether a reviewer has them or sent them back — then with what needs the
// vendor's attention and the reviewer's notes. The vendor edits the product as usual (the same set takes the
// edits) and sends the changes again, or discards them — the pictures uploaded for them go too.
import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { ExclamationTriangleIcon, CheckCircleIcon, PaperAirplaneIcon, TrashIcon } from "@heroicons/react/24/outline"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { ReviewProduct } from "../../../AdminShared/api"
import { changeLabel } from "../shared/review-marks"
import { TextArea, errorMessage } from "../../../AdminShared/ui"

export default function ChangeSetDialog({ editor, isAdmin, onClose }) {
  const t = useProductEditorTexts()
  const change = editor.read("reviewChange")
  const [notes, setNotes] = useState("")
  const [busy, setBusy] = useState("")
  if (!change) return null
  const back = change.status === "changes_requested"
  // the marks not edited since: the set goes again only once each was (the API refuses it too)
  const unchanged = back ? (change.attention || []).filter((item) => !item.resolved) : []

  const run = async (action, message) => {
    setBusy(action)
    try {
      const res = await ReviewProduct(editor.pid, action, notes.trim())
      const failed = errorMessage(res)
      if (failed) return showNotification({ message: failed, className: "bg-admin_error text-white" })
      showNotification({ message, className: "bg-admin_success text-white" })
      await editor.reload()
      onClose()
    } finally {
      setBusy("")
    }
  }
  const discard = () =>
    showPromptDialog({
      title: t`Discard your changes?`,
      message: t`The changes waiting for review and the pictures uploaded for them are deleted; the product stays as it is on sale.`,
      confirmText: t`Discard`,
      cancelText: t`Cancel`,
      onConfirm: () => run("withdraw", t`Your changes were discarded`),
    })

  return (
    <Modal isOpen onClose={onClose} variant="admin_compact" title={back ? t`Your changes need attention` : t`Your changes are in review`} styleOverrides={{ container: "bg-admin_paper w-full max-w-[560px] rounded-md border border-admin_bordered shadow-lg" }}>
      <div className="flex flex-col gap-4 p-4" data-testid="change-set-dialog">
        <p className="m-0 text-sm text-admin_text_secondary">
          {back ? t`A reviewer looked at your changes. Edit what is marked below, then send the changes again. The product stays on sale as it is until they are approved.` : t`A reviewer will look at your changes. The product stays on sale as it is until they are approved; you may keep editing meanwhile.`}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(change.changes || []).map((kind) => (
            <span key={kind} className="inline-flex items-center h-6 px-2.5 rounded-full bg-admin_neutral_1 text-xs">{changeLabel(kind, t)}</span>
          ))}
        </div>
        {back && change.notes && <div className="text-sm italic border-l-2 border-admin_bordered pl-3">“{change.notes}”</div>}
        {back && !!change.attention?.length && (
          <div className="flex flex-col gap-2" data-testid="change-set-attention">
            {change.attention.map((item) => (
              <div key={item.id} className={`flex items-start gap-2 text-sm ${item.resolved ? "text-admin_success" : "text-admin_warning"}`}>
                {item.resolved ? <CheckCircleIcon className="w-4 h-4 shrink-0 mt-0.5" /> : <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />}
                <span>
                  <b>{changeLabel(item.kind, t)}</b>
                  {item.language && <span className="ml-1 text-[11px] font-semibold uppercase">{item.language}</span>}
                  {item.note && <span className="text-admin_text">: {item.note}</span>}
                  {item.resolved && <span className="ml-1 text-xs">({t`edited`})</span>}
                </span>
              </div>
            ))}
          </div>
        )}
        {back && !isAdmin && <TextArea value={notes} onChange={setNotes} rows={2} maxLength={2000} placeholder={t`A note for the reviewer (optional)`} />}
        <div className="flex flex-wrap justify-end gap-2">
          {!isAdmin && (
            <Button variant="admin_error" onClick={discard} disabled={!!busy} data-testid="change-set-discard">
              <TrashIcon className="w-4 h-4" />
              {t`Discard changes`}
            </Button>
          )}
          {back && !isAdmin && (
            <Button variant="admin_primary" onClick={() => run("resubmit", t`Your changes have been sent for review`)} disabled={!!busy || unchanged.length > 0} title={unchanged.length ? t`Change what the reviewer marked first` : undefined} data-testid="change-set-resubmit">
              <PaperAirplaneIcon className="w-4 h-4" />
              {t`Send changes again`}
            </Button>
          )}
          {isAdmin && <Button variant="admin_secondary" onClick={onClose}>{t`Close`}</Button>}
        </div>
      </div>
    </Modal>
  )
}
